import { Link, useNavigate, useParams } from "@tanstack/react-router";

import { useArchiveNutrient, useNutrient } from "@/lib/api/nutrients";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorState } from "@/components/error-state";
import { AuditLogViewer } from "@/components/audit-log-viewer";

export function NutrientDetailPage() {
  const { nutrientId } = useParams({
    from: "/chemistry-profile/nutrients/$nutrientId",
  });
  const navigate = useNavigate();
  const q = useNutrient(nutrientId);
  const archive = useArchiveNutrient(nutrientId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;
  const n = q.data;
  const editable = !n.system_provided && !n.archived_at;

  function onArchive() {
    const reason =
      window.prompt("Reason for archiving this nutrient?") || "user archive";
    archive.mutate(reason, {
      onSuccess: () => navigate({ to: "/chemistry-profile/nutrients" }),
    });
  }

  const flags = [
    n.contains_calcium && "Contains calcium",
    n.contains_phosphate && "Contains phosphate",
    n.is_biological && "Biological",
    n.is_ph_adjuster && "pH adjuster",
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-6">
      <Link
        to="/chemistry-profile/nutrients"
        className="text-sm text-muted-foreground hover:text-foreground"
      >
        ← Nutrients
      </Link>

      <header className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{n.name}</h1>
          <p className="text-sm text-muted-foreground">
            {n.manufacturer || "—"}
            {n.system_provided && " · system-provided"}
            {n.archived_at && " · archived"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {n.system_provided && (
            <span className="rounded-md border px-2.5 py-1 text-xs text-muted-foreground">
              Read-only · system-provided
            </span>
          )}
          {editable && (
            <>
              <Link
                to="/chemistry-profile/nutrients/$nutrientId/edit"
                params={{ nutrientId: n.id }}
              >
                <Button variant="outline">Edit</Button>
              </Link>
              <Button
                variant="ghost"
                onClick={onArchive}
                disabled={archive.isPending}
              >
                {archive.isPending ? "Archiving…" : "Archive"}
              </Button>
            </>
          )}
          <Link to="/chemistry-profile/nutrients/new" search={{ from: n.id }}>
            <Button variant="outline">Clone</Button>
          </Link>
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Dosing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="Mixing order" value={n.mixing_order_rank} />
            <Row label="Base dose (ml/gal)" value={n.base_dose_ml_per_gal} />
            <Row label="ΔEC / ml / gal" value={n.delta_ec_per_ml_per_gal} />
            <Row label="ΔpH / ml / gal" value={n.delta_ph_per_ml_per_gal} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Properties
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {flags.length > 0 ? (
              <ul className="list-inside list-disc space-y-0.5 text-muted-foreground">
                {flags.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">No special properties.</p>
            )}
          </CardContent>
        </Card>
      </section>

      {n.ph_drop_alkalinity_curve.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              pH-drop alkalinity curve
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <table className="w-full max-w-md text-sm tabular-nums">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-1 font-medium">Starting pH</th>
                  <th className="py-1 font-medium">Drop / ml / gal</th>
                </tr>
              </thead>
              <tbody>
                {n.ph_drop_alkalinity_curve.map((p, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1">{p.starting_ph}</td>
                    <td className="py-1">{p.drop_per_ml_per_gal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {n.notes && (
        <section className="space-y-1">
          <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Notes
          </h2>
          <p className="whitespace-pre-wrap text-sm">{n.notes}</p>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Audit trail
        </h2>
        <AuditLogViewer entityType="nutrient" entityID={n.id} />
      </section>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value ?? "—"}</span>
    </div>
  );
}
